import React from 'react';
import styled from 'styled-components';
import { Container } from '@ui';
import { FlatButton, SkeletonButton, TransparentFlatButton } from '@ui/buttons';
import ErrorText from '@ui/ErrorText';

const Input = styled.input`
  width: 100%;
  padding: 6px 10px;

  color: ${({ theme }) => theme.themeColors.text};
  background: ${({ theme }) => theme.themeColors.appBackground};
  border: 1px solid ${({ theme }) => theme.themeColors.border};
  border-radius: 4px;
`;

const ActionsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
`;

/*
  Renames the display name only. The stored filename the download endpoints serve is untouched,
  so a renamed document still arrives on disk under its original name.
*/
export const RenameDocumentField = ({ doc, onRename, isSaving, error }) => {
  const [isEditing, setIsEditing] = React.useState(false);
  const [draft, setDraft] = React.useState(doc.filename);

  React.useEffect(() => {
    setDraft(doc.filename);
    setIsEditing(false);
  }, [doc.id, doc.filename]);

  const handleSave = async () => {
    const result = await onRename(doc, draft);
    if (result?.ok || result?.unchanged) setIsEditing(false);
  };

  if (!isEditing) {
    return <SkeletonButton onClick={() => setIsEditing(true)}>Rename</SkeletonButton>;
  }

  return (
    <Container>
      <Input
        value={draft}
        autoFocus
        aria-label="Document name"
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') handleSave();
          if (e.key === 'Escape') {
            setDraft(doc.filename);
            setIsEditing(false);
          }
        }}
      />
      <Container top={8}>
        <ActionsWrapper>
          <FlatButton disabled={isSaving} onClick={isSaving ? () => {} : handleSave}>
            {isSaving ? 'Saving…' : 'Save'}
          </FlatButton>
          <TransparentFlatButton
            onClick={() => {
              setDraft(doc.filename);
              setIsEditing(false);
            }}
          >
            Cancel
          </TransparentFlatButton>
        </ActionsWrapper>
      </Container>
      {error ? (
        <Container top={8}>
          <ErrorText>{error}</ErrorText>
        </Container>
      ) : null}
    </Container>
  );
};
